/**
 * Store assembly.
 *
 * Kept separate from `store/index.ts` so tests can build an isolated store over
 * any repository without importing — and therefore constructing — the app-wide
 * singleton (which would open a real `BroadcastChannel`).
 */

import { create } from 'zustand';
import type { StoreApi, UseBoundStore } from 'zustand';

import { crewSlug } from '../repo/index.ts';
import type { StorageLike } from '../repo/LocalRepository.ts';
import type { Repository } from '../repo/Repository.ts';
import { createBootSlice } from './boot.ts';
import { createBoardsSlice } from './slices/boards.ts';
import { createBrowseSlice } from './slices/browse.ts';
import { createFeedSlice } from './slices/feed.ts';
import { createCrewSlice } from './slices/crew.ts';
import { createCrewsSlice } from './slices/crews.ts';
import { createInvitesSlice } from './slices/invites.ts';
import { createJoinLinksSlice } from './slices/joinLinks.ts';
import { createWizardSlice } from './slices/wizard.ts';
import { createFreshnessSlice } from './slices/freshness.ts';
import { createListSlice } from './slices/list.ts';
import { createMineSlice } from './slices/mine.ts';
import { createRecsSlice } from './slices/recs.ts';
import { createWatchSlice } from './slices/watch.ts';
import { createMemberSlice } from './slices/member.ts';
import { createPlaytestsSlice } from './slices/playtests.ts';
import { createRoundSlice } from './slices/round.ts';
import { createScheduleSlice } from './slices/schedule.ts';
import { createSessionSlice } from './slices/session.ts';
import { createSteamFactsSlice } from './slices/steamFacts.ts';
import { createToastsSlice } from './slices/toasts.ts';
import { createUiSlice } from './slices/ui.ts';
import { createVideosSlice } from './slices/videos.ts';
import type { QdStore } from './types.ts';

export interface CreateStoreDeps {
  repo: Repository;
  crewId?: string;
  /** See `BootDeps.resume`. */
  resume?: StorageLike | null;
}

export type QdStoreApi = UseBoundStore<StoreApi<QdStore>>;

export function createQdStore(deps: CreateStoreDeps): QdStoreApi {
  const boot = createBootSlice({
    repo: deps.repo,
    crewId: deps.crewId ?? crewSlug(),
    resume: deps.resume,
  });
  return create<QdStore>()((...a) => ({
    ...boot(...a),
    ...createSessionSlice(...a),
    ...createUiSlice(...a),
    ...createBrowseSlice(...a),
    ...createFeedSlice(...a),
    ...createCrewSlice(...a),
    ...createCrewsSlice(...a),
    ...createInvitesSlice(...a),
    ...createJoinLinksSlice(...a),
    ...createWizardSlice(...a),
    ...createListSlice(...a),
    ...createBoardsSlice(...a),
    ...createMineSlice(...a),
    ...createRecsSlice(...a),
    ...createWatchSlice(...a),
    ...createMemberSlice(...a),
    ...createPlaytestsSlice(...a),
    ...createSteamFactsSlice(...a),
    ...createFreshnessSlice(...a),
    ...createToastsSlice(...a),
    ...createVideosSlice(...a),
    ...createRoundSlice(...a),
    ...createScheduleSlice(...a),
  }));
}
